import {
    removeNotification,
    showErrorNotification,
    showInfoNotification,
    showSuccessNotification,
    showWarningNotification
} from '../../../../widgets/notificationToaster';
import { useDialogManager } from '../../../../widgets/dialog';
import React, { Suspense, useContext, useEffect } from 'react';
import { useLazyLoadQuery } from 'react-relay';
import Loader from 'src/components/widgets/loader';
import { useSaveOptimizedScene, useSaveProduct } from 'src/components/widgets/preview3D/hooks/useSaveProduct';
import usePreview3DStore, { Preview3DStoreContext } from 'src/components/widgets/preview3D/store';
import theme from 'src/shared/theme';
import styled, { css } from 'styled-components';
import SettingsIcon from '../../../../../../src/shared/icons/cog-solid.svg';
import LightsIcon from '../../../../../../src/shared/icons/lightbulb.svg';
import ObjectIcon from '../../../../../../src/shared/icons/object-group.svg';
import { ascComparator, T, toLocalID } from '../../../../../shared/helpers';
import { useProductConfigurationState } from '../../state';
import ComposerAttributeContentPage from '../configurator-components/composer-editors/attributes/composerAttributePage';
import { ComposerAttributeType, ComposerEcommercePlatform } from '../configurator-components/composer-editors/attributes/composerAttributesModels';
import ComposerStarted from '../configurator-components/composer-editors/attributes/composerStarted';
import AttributesEditor from '../configurator-components/composer-editors/attributesEditor';
import PredesignTemplateEditor from '../configurator-components/composer-editors/predesignTemplateEditor';
import QuantityEditor from '../configurator-components/composer-editors/quantityEditor';
import ComposerSettings from '../configurator-components/composer-editors/settings/composerSettings';
import { ComposerTemplateContentPage } from '../configurator-components/composer-editors/templates/composerTemplateContentPage';
import HeaderToolbar from '../configurator-components/headerToolbar';
import LeftVerticalToolbar, { StepsTools } from '../configurator-components/leftVerticalToolbar';
import { composerQueriesEcommerceSettingsQuery } from './composer/__generated__/composerQueriesEcommerceSettingsQuery.graphql';
import { composerQueriesFontQuery } from './composer/__generated__/composerQueriesFontQuery.graphql';
import { composerQueriesListTemplatesQuery } from './composer/__generated__/composerQueriesListTemplatesQuery.graphql';
import { composerEcommerceSettingsQuery, composerFontQuery, composerListTemplatesQuery } from './composer/composerQueries';
import useComposerStore from './composer/composerStore';
import ComposerGenerateAIAttributeDialog from './composer/dialogs/composerGenerateAIAttributeDialog';
import ComposerSaveAttributeDialog from './composer/dialogs/save/composerSaveAttributeDialog';
import { adjustDisplayOrder } from './composer/helpers/composerHelpers';
import { ComposerAttributeNodeAdapter, ComposerTemplateNodeAdapter } from './composer/helpers/composerNodesAdapter';
import useSaveDesign from './composer/helpers/useSaveDesign';

const Container = styled.div`
    position: fixed;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background-color: rgb(246, 248, 249);
    z-index: 30;
    width: 100%;
    & > * {
        width: 100%;
    }
`;

const MainContentContainer = styled.div`
    flex: 1;
    overflow: hidden;
`;

const MainContentLayout = styled.div`
    display: flex;
    flex-direction: row;
    gap: 12px;
    height: 100%;
    max-height: 100%;
    background: #f6f8f9;
`;

const PreviewContainer = styled.div`
    flex: 1;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const LeftVerticalToolbarContainer = styled.div<{ $hidden?: boolean; $hiddenSidebar?: boolean }>`
    padding: 0 12px 12px;
    overflow-y: auto;
    width: 394px;
    padding-right: 0;

    border-right: 1px solid ${theme.app.borderColor};
    ${(props) =>
        props.$hidden &&
        css`
            display: none;
        `}

    ${(props) =>
        props.$hiddenSidebar &&
        css`
            border-right: none;
            width: 120px;
        `}
`;

const Composer = () => {
    const { showDialog, closeDialog } = useDialogManager();
    const { state } = useProductConfigurationState();
    const [zkProduct, zkScene, sceneEditor, setZkProduct, incrementSaveVersion] = usePreview3DStore((x) => [
        x.zkProduct,
        x.zkScene,
        x.sceneEditor,
        x.setZkProduct,
        x.incrementSaveVersion
    ]);
    const [saveProduct] = useSaveProduct();
    const [saveOptimizedScene] = useSaveOptimizedScene();
    const [saveDesign] = useSaveDesign();
    const fetchedEcommerceSettings = useLazyLoadQuery<composerQueriesEcommerceSettingsQuery>(
        composerEcommerceSettingsQuery,
        {},
        {
            fetchPolicy: 'store-or-network'
        }
    );

    const fetchedTemplates = useLazyLoadQuery<composerQueriesListTemplatesQuery>(
        composerListTemplatesQuery,
        {
            productId: toLocalID(state.product.id) ?? 0
        },
        {
            fetchPolicy: 'store-or-network'
        }
    );

    const queryFont = useLazyLoadQuery<composerQueriesFontQuery>(
        composerFontQuery,
        {},
        {
            fetchPolicy: 'store-or-network'
        }
    );

    const preview3DStore = useContext(Preview3DStoreContext);
    const [
        storedAttributes,
        storedEcommerceSettings,
        storedTemplates,
        storedAreas,
        storedDesigns,
        sceneMaterials,
        sceneMeshes,
        sceneCameras,
        setModel,
        setFonts,
        initializeStoreAttributeInfo,
        initializeEcommerceSettings,
        updateAttributes,
        initializeStoreTemplateInfo,
        setSceneMaterials,
        setSceneObjects,
        setSceneCameras
    ] = useComposerStore((x) => [
        x.attributes,
        x.ecommerceSettings,
        x.templates,
        x.areas,
        x.designs,
        x.sceneMaterialList,
        x.sceneObjectList,
        x.sceneCameraList,
        x.setModel,
        x.setFonts,
        x.intializeAttributesInfo,
        x.setEcommerceSettings,
        x.updateAttribute,
        x.initializeTemplateInfo,
        x.setSceneMaterialList,
        x.setSceneObjectList,
        x.setSceneCameraList
    ]);

    const [currentTab, setCurrentTab] = React.useState<string>('attributes');
    const [isLoading, setIsLoading] = React.useState<boolean>(true);
    const [isSaving, setIsSaving] = React.useState<boolean>(false);
    const hasAttributes = storedAttributes.length > 0;

    useEffect(() => {
        if (!zkProduct || !zkScene || !fetchedTemplates || !queryFont) return;

        setModel(zkProduct);

        setFonts(queryFont);

        const modelAttributes = zkProduct.get('attributes') || [];
        const attributes = modelAttributes.map(ComposerAttributeNodeAdapter.toNode);
        const steps = (zkProduct.get('attributeSteps') || []).map(ComposerAttributeNodeAdapter.toStepNode);
        const groups = (zkProduct.get('attributeGroups') || []).map(ComposerAttributeNodeAdapter.toGroupNode);

        const adjustedAttributes = adjustDisplayOrder([ComposerAttributeNodeAdapter.adjustValues(attributes), steps, groups].flat());
        initializeStoreAttributeInfo(modelAttributes, adjustedAttributes);

        const templates =
            fetchedTemplates?.designTemplates?.map((te) =>
                ComposerTemplateNodeAdapter.toNode(toLocalID(te.id) ?? 0, te.name, te.design, null, zkProduct, zkProduct.get('colors').at(0)?.get('sides'))
            ) || [];
        const areas =
            zkProduct
                .get('colors')
                .at(0)
                ?.get('sides')
                .map((side) => ComposerTemplateNodeAdapter.toNodeArea(side, zkProduct.get('scenes').at(0).get('meshSides'))) || [];
        initializeStoreTemplateInfo(templates, areas);
        setIsLoading(false);
    }, [zkProduct, zkScene, fetchedTemplates, queryFont]);

    useEffect(() => {
        if (!sceneEditor || !sceneEditor?.scene?.meshes) return;

        setSceneObjects(sceneEditor!.scene!.meshes);
        setSceneMaterials(sceneEditor!.scene!.materials);
        setSceneCameras(sceneEditor!.scene!.cameras);
    }, [sceneEditor]);

    useEffect(() => {
        const ecommerceSettingsData = fetchedEcommerceSettings?.ecommerceSettings;
        if (!ecommerceSettingsData) return;

        initializeEcommerceSettings({
            ecommercePlatform: ecommerceSettingsData.ecommerceID ?? (-1 as ComposerEcommercePlatform)
        });
    }, [fetchedEcommerceSettings]);

    const ComposerTools: StepsTools[] = [
        {
            tool: T._('Attributes', 'Admin'),
            icon: <ObjectIcon />,
            tooltip: T._('Attributes', 'Admin'),
            onClick: () => {
                setCurrentTab('attributes');
            },
            component: AttributesEditor
        },
        // { MERGED WITH PREDESIGN-TEMPLATES
        // 	tool: 'customizable-area',
        // 	icon: <MaterialsIcon />,
        // 	tooltip: T._('Customizable Area', 'Admin'),
        // 	onClick: () => {
        // 		setCurrentTab('customizable-area');
        // 	},
        // 	component: CustomizableAreasEditor
        // },
        {
            tool: T._('Predesign Templates', 'Admin'),
            icon: <LightsIcon />,
            tooltip: T._('Predesign Templates', 'Admin'),
            onClick: () => {
                setCurrentTab('predesign-templates');
            },
            component: PredesignTemplateEditor
        },
        {
            tool: T._('Settings', 'Admin'),
            icon: <SettingsIcon />,
            tooltip: T._('Settings', 'Admin'),
            onClick: () => {
                setCurrentTab('settings');
            },
            component: QuantityEditor
        }
    ];

    const showComposerAttributeCreationDialog = () => {
        showDialog(
            'composerCreateAttribute',
            <ComposerSaveAttributeDialog
                onConfirm={(attribute) => {
                    if (!attribute) return;
                    attribute.isNew = true;
                    updateAttributes(attribute);
                    closeDialog('composerCreateAttribute');
                }}
                productCode={state.product.code}
                salesChannelID={state.salesChannelID}
                sceneCameraList={zkScene?.get?.('cameraLocations')?.models?.sort(ascComparator((item) => item.get('name') ?? '')) ?? []}
                meshList={sceneMeshes.filter((x) => sceneEditor?.isRealMesh(x)).sort(ascComparator((item) => item.name ?? '')) ?? []}
                edit={false}
                passedAttribute={null}
                fixedType={ComposerAttributeType.Attribute}
                selectedPlatformAttribute={null}
                ecommerceSettings={storedEcommerceSettings || null}
            />
        );
    };

    const showComposerAttributeAICreationDialog = () => {
        showDialog(
            'composerGenerateAIAttribute',
            <Preview3DStoreContext.Provider value={preview3DStore}>
                <ComposerGenerateAIAttributeDialog
                    materialList={sceneMaterials}
                    meshList={sceneMeshes}
                    onClose={() => closeDialog('composerGenerateAIAttribute')}
                    sceneEditor={preview3DStore?.getState().sceneEditor}
                    onConfirm={(attributes) => {
                        if (!attributes) return;
                        attributes.forEach((attribute) => {
                            attribute.isNew = true;
                            updateAttributes(attribute);
                        });
                        closeDialog('composerGenerateAIAttribute');
                    }}
                />
            </Preview3DStoreContext.Provider>
        );
    };
    if (isLoading) {
        return (
            <Container>
                <Loader />
            </Container>
        );
    }

    const currentTabContent = () => {
        if (currentTab === 'attributes') {
            return hasAttributes ? (
                <PreviewContainer>
                    <ComposerAttributeContentPage />
                </PreviewContainer>
            ) : (
                <ComposerStarted onAddAttributes={showComposerAttributeCreationDialog} onGenerateAttributesWithAI={showComposerAttributeAICreationDialog} />
            );
        } else if (currentTab === 'predesign-templates') {
            return (
                <PreviewContainer>
                    <ComposerTemplateContentPage />
                </PreviewContainer>
            );
        } else if (currentTab === 'settings') {
            return <ComposerSettings />;
        }
        return <>test</>;
    };

    const onSave = async () => {
        console.log('Save composer');
        if (!zkProduct || !storedAttributes) return;
        const info = showInfoNotification({
            message: T._('Saving product, please wait...', 'Admin')
        });

        // Validation: Check if any attribute has no options
        const attributesWithoutOptions = storedAttributes.filter(
            (attr) => attr.type === ComposerAttributeType.Attribute && (!attr.options || attr.options.length === 0)
        );

        if (attributesWithoutOptions.length > 0) {
            const attributeNames = attributesWithoutOptions.map((attr) => attr.name).join(', ');
            console.log('🔍 Validation failed - Attributes without options:', attributeNames);
            showWarningNotification({
                message: T._('The following attributes must have at least one option before saving:', 'Admin') + ' ' + attributeNames
            });
            return;
        }

        // setIsSaving(true);
        try {
            console.log('Attributes to process:', storedAttributes);
            const { attributes, groups, steps } = ComposerAttributeNodeAdapter.saveAttributes(storedAttributes);
            console.log('Attributes to save:', attributes, groups, steps);
            zkProduct.set('attributes', attributes);
            zkProduct.set('attributeGroups', groups);
            zkProduct.set('attributeSteps', steps);

            const { sides, modelSceneMeshSides } = ComposerTemplateNodeAdapter.saveTemplates(storedTemplates, storedAreas);

            zkProduct.get('scenes').at(0).set('meshSides', modelSceneMeshSides);
            zkProduct.get('colors').at(0).set('sides', sides);

            setZkProduct(zkProduct);
            console.log('Saving product:', zkProduct);
            await saveProduct({}).catch((e) => {
                console.error(e);
                showErrorNotification({
                    message: T._('Failed to save the product', 'Admin')
                });
            });

            await saveOptimizedScene().catch((e) => {
                console.error(e);
                showErrorNotification({
                    message: T._('Failed to save the 3D scene', 'Admin')
                });
            });

            for (const design of Array.from(storedDesigns.values())) {
                console.log('Saving design:', design);
                await saveDesign(design).catch((e) => {
                    console.error(e);
                    showErrorNotification({
                        message: T._('Failed to save a design', 'Admin')
                    });
                });
            }
            removeNotification(info);
            showSuccessNotification({
                message: T._('Composer settings saved successfully', 'Admin')
            });
        } catch (error) {
            console.error('Error during save:', error);
            showErrorNotification({
                message: T._('An error occurred while saving the product. Please try again.', 'Admin')
            });
        } finally {
            removeNotification(info);
        }
        return false;
    };

    if (isSaving) {
        return (
            <Container>
                <Loader />
            </Container>
        );
    }

    return (
        <Container>
            <HeaderToolbar onSave={onSave} />
            <MainContentContainer>
                <MainContentLayout>
                    <Suspense fallback={<Loader />}>
                        <LeftVerticalToolbarContainer $hidden={false} $hiddenSidebar={currentTab === 'settings'}>
                            <LeftVerticalToolbar tools={ComposerTools} hiddenSidebar={currentTab === 'settings'} />
                        </LeftVerticalToolbarContainer>
                        {currentTabContent()}
                    </Suspense>
                </MainContentLayout>
            </MainContentContainer>
        </Container>
    );
};
export default Composer;
